import { Fragment, useEffect, useId, useRef, useState } from 'react';
import type { FC, FormEvent, KeyboardEvent } from 'react';

import { useAuiState } from '@assistant-ui/react';
import { ChevronRightIcon } from '@interstate/components/Icons/ChevronRightIcon';
import { XMarkIcon } from '@interstate/components/Icons/XMarkIcon';
import { AiLogo } from '@makemydeal/dr-shared-components';

import {
    DEAL_ASSISTANT_FAILURE_MESSAGE,
    DEAL_ASSISTANT_LOADING_LABEL,
    DEAL_ASSISTANT_TYPING_DELAY_MS,
    DEAL_ASSISTANT_PANEL_PLACEHOLDER,
    DEAL_ASSISTANT_PANEL_TITLE,
    DEAL_ASSISTANT_UNAVAILABLE_LABELS,
    DEAL_ASSISTANT_WELCOME_LINKS,
    DEAL_ASSISTANT_WELCOME_TEXT
} from '../constants';
import { useDealAssistant } from '../state/DealAssistantContext';
import { DealAssistantTheme } from './DealAssistantTheme';
import {
    Composer,
    ComposerInput,
    FailureMessage,
    Message,
    MessageLogo,
    MessageRow,
    PanelCloseButton,
    PanelFooter,
    PanelHeader,
    PanelHeading,
    PanelLogo,
    PanelReason,
    PanelRoot,
    PanelSubtitle,
    PanelThread,
    PanelTitle,
    SendButton,
    TypingIndicator,
    WelcomeBubble,
    WelcomeLink,
    WelcomeLinks,
    WelcomeText
} from './DealAssistantPanel.style';

const textOf = (content: readonly { type: string }[]): string =>
    content
        .filter((part) => part.type === 'text')
        .map((part) => (part as unknown as { text: string }).text)
        .join('');

export const DealAssistantPanel: FC = () => {
    const {
        isOpen,
        status,
        isAvailable,
        unavailableReason,
        dealStructureSnapshotLabel,
        failedMessageIds,
        close,
        sendMessage,
        trackWelcomeLinkClick
    } = useDealAssistant();
    const messages = useAuiState((state) => state.thread.messages);
    const [text, setText] = useState('');
    const [isTypingVisible, setIsTypingVisible] = useState(false);
    const threadRef = useRef<HTMLDivElement>(null);
    const reasonId = useId();
    const isLoading = status === 'loading';

    const visibleMessages = messages
        .map(({ id, role, content }) => ({ id, role, text: textOf(content) }))
        .filter(({ text: messageText }) => messageText.length > 0);

    useEffect(() => {
        if (!isLoading) {
            setIsTypingVisible(false);
            return undefined;
        }
        const timer = setTimeout(() => setIsTypingVisible(true), DEAL_ASSISTANT_TYPING_DELAY_MS);
        return () => clearTimeout(timer);
    }, [isLoading]);

    const showTyping = isLoading && isTypingVisible && visibleMessages[visibleMessages.length - 1]?.role !== 'assistant';

    useEffect(() => {
        const thread = threadRef.current;
        if (thread) {
            thread.scrollTop = thread.scrollHeight;
        }
    }, [isOpen, visibleMessages.length, showTyping, failedMessageIds.length]);

    if (!isOpen) {
        return null;
    }

    const canSend = text.trim().length > 0 && !isLoading && isAvailable;

    const send = () => {
        if (!canSend) {
            return;
        }
        const question = text.trim();
        setText('');
        void sendMessage(question);
    };

    const handleSubmit = (event: FormEvent) => {
        event.preventDefault();
        send();
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
            event.preventDefault();
            send();
        }
    };

    return (
        <DealAssistantTheme>
            <PanelRoot aria-label={DEAL_ASSISTANT_PANEL_TITLE} data-testid="deal-assistant-panel">
                <PanelHeader>
                    <PanelLogo>
                        <AiLogo className="deal-assistant-panel-logo" aria-hidden="true" />
                    </PanelLogo>
                    <PanelHeading>
                        <PanelTitle>{DEAL_ASSISTANT_PANEL_TITLE}</PanelTitle>
                        {dealStructureSnapshotLabel && (
                            <PanelSubtitle data-testid="deal-assistant-panel-subtitle">{dealStructureSnapshotLabel}</PanelSubtitle>
                        )}
                    </PanelHeading>
                    <PanelCloseButton
                        type="button"
                        aria-label="Close Deal AI"
                        data-testid="deal-assistant-panel-close"
                        onClick={close}
                    >
                        <XMarkIcon />
                    </PanelCloseButton>
                </PanelHeader>
                <PanelThread ref={threadRef} data-testid="deal-assistant-thread" aria-live="polite">
                    <MessageRow>
                        <MessageLogo>
                            <AiLogo className="deal-assistant-message-logo" aria-hidden="true" />
                        </MessageLogo>
                        <WelcomeBubble data-testid="deal-assistant-welcome">
                            <WelcomeText>{DEAL_ASSISTANT_WELCOME_TEXT}</WelcomeText>
                            <WelcomeLinks>
                                {DEAL_ASSISTANT_WELCOME_LINKS.map((label) => (
                                    <WelcomeLink key={label} type="button" onClick={() => trackWelcomeLinkClick(label)}>
                                        {label}
                                    </WelcomeLink>
                                ))}
                            </WelcomeLinks>
                        </WelcomeBubble>
                    </MessageRow>
                    {visibleMessages.map(({ id, role, text: messageText }) => (
                        <Fragment key={id}>
                            <MessageRow $role={role}>
                                {role !== 'user' && (
                                    <MessageLogo>
                                        <AiLogo className="deal-assistant-message-logo" aria-hidden="true" />
                                    </MessageLogo>
                                )}
                                <Message $role={role} data-testid={`deal-assistant-message-${role}`}>
                                    {messageText}
                                </Message>
                            </MessageRow>
                            {failedMessageIds.includes(id) && (
                                <MessageRow>
                                    <MessageLogo>
                                        <AiLogo className="deal-assistant-message-logo" aria-hidden="true" />
                                    </MessageLogo>
                                    <FailureMessage data-testid="deal-assistant-failure">
                                        {DEAL_ASSISTANT_FAILURE_MESSAGE}
                                    </FailureMessage>
                                </MessageRow>
                            )}
                        </Fragment>
                    ))}
                    {showTyping && (
                        <MessageRow>
                            <MessageLogo>
                                <AiLogo className="deal-assistant-message-logo" aria-hidden="true" />
                            </MessageLogo>
                            <TypingIndicator
                                role="status"
                                aria-label={DEAL_ASSISTANT_LOADING_LABEL}
                                data-testid="deal-assistant-loading"
                            >
                                <i />
                                <i />
                                <i />
                            </TypingIndicator>
                        </MessageRow>
                    )}
                </PanelThread>
                <PanelFooter>
                    {unavailableReason && (
                        <PanelReason id={reasonId} role="status" data-testid="deal-assistant-panel-reason">
                            {DEAL_ASSISTANT_UNAVAILABLE_LABELS[unavailableReason]}
                        </PanelReason>
                    )}
                    <Composer onSubmit={handleSubmit}>
                        <AiLogo className="deal-assistant-composer-logo" aria-hidden="true" />
                        <ComposerInput
                            value={text}
                            placeholder={DEAL_ASSISTANT_PANEL_PLACEHOLDER}
                            aria-label={DEAL_ASSISTANT_PANEL_PLACEHOLDER}
                            data-testid="deal-assistant-composer-input"
                            aria-describedby={unavailableReason ? reasonId : undefined}
                            onChange={(event) => setText(event.target.value)}
                            onKeyDown={handleKeyDown}
                        />
                        <SendButton type="submit" aria-label="Send" data-testid="deal-assistant-send" disabled={!isAvailable}>
                            <ChevronRightIcon />
                        </SendButton>
                    </Composer>
                </PanelFooter>
            </PanelRoot>
        </DealAssistantTheme>
    );
};
